import { useCallback, useState } from 'react';
import { Plug, Wifi, WifiOff, RotateCw, ShieldX } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Pill } from '@/components/widgets/Pill';
import { useChannel } from '@/realtime/use-channel';
import type { CtidStatusEvent } from '@/realtime/types';
import { fmtTime } from '@/lib/format';

interface LogItem {
  id: number;
  type: string;
  ctidId: string;
  detail: string;
  ts: string;
  tone: 'up' | 'down' | 'warn' | 'brand' | 'neutral';
  icon: React.ReactNode;
}

let _id = 0;

function eventToLogItem(ev: CtidStatusEvent): LogItem | null {
  const id = ++_id;
  const ts = new Date().toISOString();
  if (!('type' in ev) || !ev.type) return null;
  switch (ev.type) {
    case 'connected':
      return { id, type: 'connected', ctidId: ev.ctidId, detail: `已认证 ${ev.accountsAuthed} 账户(${ev.kind})`, ts, tone: 'up', icon: <Wifi className="h-3.5 w-3.5" /> };
    case 'disconnected':
      return { id, type: 'disconnected', ctidId: ev.ctidId, detail: `${ev.kind} · ${ev.reason ?? '原因未知'}`, ts, tone: 'down', icon: <WifiOff className="h-3.5 w-3.5" /> };
    case 'reconnected':
      return { id, type: 'reconnected', ctidId: ev.ctidId, detail: `${ev.kind} · 重连 ${ev.attempts} 次后恢复`, ts, tone: 'warn', icon: <RotateCw className="h-3.5 w-3.5" /> };
    case 'auth_failed':
      return { id, type: 'auth_failed', ctidId: ev.ctidId, detail: `${ev.kind} · ${ev.errorCode ?? ''} ${ev.description ?? ''}`, ts, tone: 'down', icon: <ShieldX className="h-3.5 w-3.5" /> };
    case 'token_refreshed':
      return { id, type: 'token_refreshed', ctidId: ev.ctidId, detail: `新过期时间 ${new Date(ev.newExpiresAt).toLocaleDateString('zh-CN')}`, ts, tone: 'up', icon: <RotateCw className="h-3.5 w-3.5" /> };
    case 'token_invalid':
      return { id, type: 'token_invalid', ctidId: ev.ctidId, detail: `${ev.errorCode ?? ''} 需要管理员介入`, ts, tone: 'down', icon: <ShieldX className="h-3.5 w-3.5" /> };
    default:
      return null;
  }
}

/** ctid_status 频道实时事件日志 */
export function CtidEventLog() {
  const [items, setItems] = useState<LogItem[]>([]);

  const onEvent = useCallback((ev: CtidStatusEvent) => {
    const item = eventToLogItem(ev);
    if (!item) return;
    setItems((prev) => [item, ...prev].slice(0, 50));
  }, []);
  useChannel('ctid_status', onEvent, [onEvent]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Plug className="h-4 w-4 text-brand" />
          连接事件日志 · {items.length} 条
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="max-h-96 overflow-y-auto scrollbar-thin">
          {items.length === 0 && (
            <div className="px-4 py-12 text-center text-ink-3 text-sm">尚未收到事件 · 等待 ctid_status 频道推送</div>
          )}
          {items.map((it) => (
            <div key={it.id} className="px-4 py-2 border-t border-line first:border-t-0 flex items-start gap-3 row-hover">
              <div className="mt-0.5 text-ink-3">{it.icon}</div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <Pill tone={it.tone} className="text-[10px]">{it.type}</Pill>
                  <span className="text-xs num text-ink-2">cTID {it.ctidId}</span>
                  <span className="text-xs text-ink-3 num ml-auto">{fmtTime(it.ts)}</span>
                </div>
                <div className="mt-0.5 text-xs text-ink-1 num truncate">{it.detail}</div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
